import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const REASONS = [
  { key: "harassment", label: "Harassment" },
  { key: "spam", label: "Spam" },
  { key: "scam", label: "Scam" },
  { key: "sexual", label: "Sexual or explicit behavior" },
  { key: "threats", label: "Threats or dangerous behavior" },
  { key: "inappropriate", label: "Inappropriate behavior" },
  { key: "impersonation", label: "Impersonation" },
  { key: "other", label: "Other" },
];

export default function ReportUserDialog({ open, onOpenChange, username, onSubmitted }) {
  const { t } = useLanguage();
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${username}/report`, { reason, details: details.trim() || null });
      toast.success(t("Thanks — our moderators will take a look."));
      setDetails("");
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="report-user-dialog">
        <DialogTitle className="flex items-center gap-2 font-heading text-lg font-extrabold text-slate-900">
          <Flag className="h-4 w-4 text-pink-500" /> {t("Report")} @{username}
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          {t("Reports are confidential. The reported user won't see who reported them.")}
        </DialogDescription>
        <div className="mt-3 space-y-2" role="radiogroup">
          {REASONS.map((r) => (
            <label
              key={r.key}
              className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 text-sm ${
                reason === r.key ? "border-pink-400 bg-pink-50" : "border-slate-100"
              }`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => setReason(r.key)}
                data-testid={`report-user-reason-${r.key}`}
                className="h-4 w-4 accent-pink-500"
              />
              <span className="font-semibold text-slate-800">{t(r.label)}</span>
            </label>
          ))}
        </div>
        <Textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1000}
          placeholder={t("Add more context (optional)")}
          data-testid="report-user-details"
          className="mt-3 min-h-24 rounded-2xl"
        />
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            {t("Cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="btn-submit-user-report"
            className="rounded-full bg-pink-500 text-white hover:bg-pink-600"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Submit report")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
